import { ContactResponse } from '@business/entities/contact';
import { CompactDealResponse, DealResponse } from '@business/entities/deal';
import {
  CompactOrgResponse,
  OrgAddressResponse,
  OrgBillingType,
  OrgResponse,
  PaymentMethodResponse,
  PaymentMethodType,
} from '@business/entities/organization';
import { ProjectResponse } from '@business/entities/project';
import { FieldType } from '@business/entities/settingFields';
import {
  CompactInvoiceResponse,
  InvoiceTypeEnum,
  SalesInvoiceType,
  SalesStatusType,
  TransactionInvoiceResponse,
} from '@business/entities/TransactionInvoice';
import { PageContextInfo, PCType } from '@core/components/PageContext';
import { SettingsConfig } from '@core/utils/config';
import { Formatter } from '@core/utils/formatter';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { OrganizationStatusResponse } from '@business/entities/organization_status';
import { HWDealResponse } from '@business/entities/home';

// export function isBannerStatus(status: OrganizationStatusResponse) {
//   const settings = SettingsConfig.current();
//   if (status.name === 'Do Not Message' || status.name === 'Do Not Sell' || status.name === 'Do Not Bill') {
//     return true;
//   }
//   return false;
// }

export function getStatuses() {
  return SettingsConfig.current()?.orgStatuses || [];
}

export function getDefaultAddress(org: OrgResponse | null) {
  if (!org) {
    return null;
  }
  return org.addresses.filter(a => a.isDefault)[0] || null;
}

export function getAddressString(address: Partial<OrgAddressResponse>, joinBy = '\n') {
  return R.compact([
    address.name,
    address.address,
    address.addressLineTwo,
    // `${address.city}, ${address.state} ${address.postal}`,
    R.compact([address.city, address.state]).join(', ') + (address.postal ? ' ' + address.postal : ''),
    address.country,
    address.phone,
    address.notes,
  ])
    .join(joinBy)
    .trim();
}

export function isValidAddress(address: OrgAddressResponse) {
  if (R.isEmpty(address.address) || R.isEmpty(address.city) || R.isEmpty(address.state) || R.isEmpty(address.postal) || R.isEmpty(address.country)) {
    return false;
  }

  return true;
}

export function getMethodString(method: PaymentMethodResponse) {
  if (method.type === PaymentMethodType.ach) {
    return `🏦 ${method.cardType} •••• •••• •••• ${method.cardDigits}`;
  }
  return `💳 ${method.cardType} •••• •••• •••• ${method.cardDigits} (${method.cardExpiry})`;
}

export function getOrgIdFromContext(info: PageContextInfo | undefined) {
  if (!info) return null;
  switch (info.type) {
    case PCType.organization:
      return (info.object as OrgResponse).id;
    // case PCType.ticket: return (info.object as TicketResp).id;
    case PCType.contact:
      return (info.object as ContactResponse).organization.id;
    case PCType.project:
      return (info.object as ProjectResponse).organization.id;
    case PCType.deal:
      return (info.object as DealResponse).orgId;
    // case PCType.marketingBoard: return (info.object as OrgResponse).id;
  }
}

export function getLastActivity(org: OrgResponse | CompactOrgResponse | DealResponse | CompactDealResponse | HWDealResponse) {
  if (org.extra.lastActivityDateTime && org.extra.lastActivityDetail) {
    return `${Formatter.formatTime(org.extra.lastActivityDateTime)} - ${org.extra.lastActivityDetail}`;
  }
  return undefined;
}

export function getBillingTypes() {
  return ['project', 'subscription'];
}

export function getFilterCustomFields() {
  const settings = SettingsConfig.current();
  if (!settings) return [];
  return R.pipe(
    settings.customFields,
    R.ops.filter(f => f.filter),
    R.ops.filter(f => f.for === 'organizations'),
    R.ops.filter(f => [FieldType.dropdown, FieldType.multiOptions, FieldType.singleOption].includes(f.type)),
  );
  //   return settings.customFields.filter(f => f.for === 'organizations' && f.type === FieldType.dropdown);
}

export function getOrgBillingTypes() {
  return [OrgBillingType.self, OrgBillingType.parent];
}

export function getOrgBillingTypeTitle(type: OrgBillingType) {
  switch (type) {
    case OrgBillingType.self:
      return 'Bill this customer';
    case OrgBillingType.parent:
      return 'Bill with parent';
  }
  return type;
}

export function getSalesStatusIntent(status: SalesStatusType) {
  if (R.includes([SalesStatusType.refunded, SalesStatusType.credited], status)) {
    return 'dark';
  }
  if (R.includes([SalesStatusType.failed, SalesStatusType.overdue], status)) {
    return 'danger';
  }
  if (R.includes([SalesStatusType.paid, SalesStatusType.closed], status)) {
    return 'success';
  }
  if (R.includes([SalesStatusType.partiallyPaid, SalesStatusType.open], status)) {
    return 'info';
  }
  return 'info';
}
export function getAllSalesStatus() {
  return [
    SalesStatusType.paid,
    SalesStatusType.failed,
    SalesStatusType.closed,
    SalesStatusType.overdue,
    SalesStatusType.partiallyPaid,
    SalesStatusType.open,
  ];
}
export function getAllSalesTypes() {
  return [SalesInvoiceType.invoice, SalesInvoiceType.salesReceipt, SalesInvoiceType.payment, SalesInvoiceType.refund];
}

export function canProcessInvoice(invoice: TransactionInvoiceResponse | CompactInvoiceResponse) {
  if (!!invoice.isTemplate && invoice.type !== InvoiceTypeEnum.order) {
    return false;
  }

  if (toMoment(invoice.dueDate).isAfter(toMoment())) {
    return false;
  }

  if (invoice.remaining > 0 && R.includes([InvoiceTypeEnum.subscription, InvoiceTypeEnum.order, InvoiceTypeEnum.project], invoice.type)) {
    return true;
  }

  return false;
}
